import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';
import { formatISO9075 } from 'date-fns';

import { mifidApi } from '@/api/mifid/index.ts';
import { getWorkspaceMatchFulfilled } from '@/api/workspace/index.ts';
import { mapToCashTile } from '@/domain/FxCash/FxCash.utils.ts';
import { cashRepriceQuoteCashReducer } from '@/store/blotter/workflows/cashRepriceQuote/extraReducers';
import { cashApi } from '@/store/cash/api/index.ts';
import { buildDuplicatedCashTile, generateCashSpotTile } from '@/store/cash/helpers/tile.ts';
import { updateCashFromTradeCapture } from '@/store/cash/helpers/trade-capture.ts';
import type { CashById, CashQuote, FxCash, RfsPrice } from '@/store/cash/state.ts';
import { updateFixingSource } from '@/store/cash/workflows/ndf-fixing-references/cash-extraReducers.ts';
import { espStreamUnsubscribed } from '@/store/esp/slice.ts';
import { reduceCashOnFetchSharedWorkspace } from '@/store/sharedWorkspace/workflows/getSharedTab.workflow.ts';
import { reduceCashStateOnSharedTabRemoved } from '@/store/sharedWorkspace/workflows/onSharedTabRemoved.workflow.ts';
import { addTile, duplicateTile, removeTile, updateTileProductType } from '@/store/tiles/slice.ts';
import type { TradeCaptureChecked } from '@/store/types.ts';
import type { UserPreferencesState } from '@/store/userPreferences/state.ts';
import type { PredealCheck, PredealCheckStatusIcon, PredealCheckStatusLabel } from '@/types/common.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import { isIsoDate, type Timestamp } from '@/utils/date.ts';
import type { ReceivedCashQuoteReply } from '@/utils/rfs/type.ts';

// 1 PPS = 10 forward points (FX convention: 1 pip = 10 points)
const PPS_TO_POINTS_FACTOR = 10;

export const cashSlice = createSlice({
  name: 'cash',
  reducerPath: 'cash',

  initialState: {} as CashById,

  reducers: {
    // Action used when the notional value is undefined or 0,
    // and so we can't call Trade capture to update the value without notional
    cashNotionalCurrencyChanged: (
      state,
      action: PayloadAction<{ tileId: string; notionalCurrencyIndex: CurrencyIndex }>,
    ) => {
      const tile = state[action.payload.tileId];

      if (isNotDefined(tile) || tile.properties.notionalCurrencyIndex === action.payload.notionalCurrencyIndex) {
        return;
      }

      tile.properties.notionalCurrencyIndex = action.payload.notionalCurrencyIndex;
    },

    cashSpotTieringUpdated: (
      state,
      action: PayloadAction<{ tileId: string; tiering: string; isDefaultTiering: boolean }>,
    ) => {
      const tile = state[action.payload.tileId];

      if (isNotDefined(tile) || (tile.type !== 'FxSpot' && tile.type !== 'FxNdf')) {
        return;
      }

      tile.properties.margin = {
        isTieringSuccess: true,
        tiering: action.payload.tiering,
        isEspDefaultTiering: action.payload.isDefaultTiering,

        commercialMarginSpotPoints: undefined,
        commercialMarginDefaultSpotPoints: undefined,
        commercialMarginAmountCurrencyIndex: 2,
      };
    },

    cashSpotTieringError: (state, action: PayloadAction<{ tileId: string }>) => {
      const tile = state[action.payload.tileId];

      if (isNotDefined(tile) || (tile.type !== 'FxSpot' && tile.type !== 'FxNdf')) {
        return;
      }

      tile.properties.margin = {
        isTieringSuccess: false,
      };
    },

    cashClientIdUpdated: (
      state,
      action: PayloadAction<
        | { tileId: string; clientId: number | undefined; isTabClientOverriden: true }
        | { tileId: string; clientId: number; isTabClientOverriden: false }
      >,
    ) => {
      const tile = state[action.payload.tileId];

      if (isNotDefined(tile) || tile.clientId === action.payload.clientId) {
        return;
      }

      if (action.payload.isTabClientOverriden === true) {
        tile.clientId = action.payload.clientId;
      }

      // reset the tiering when clientId changes to force to request the tiering for the newt clientID
      if (tile.type === 'FxSpot' && isDefined(tile.properties.margin)) {
        tile.properties.margin = undefined;
      }
    },

    cashCommercialSpotMarginUpdated: (state, action: PayloadAction<{ tileId: string; bid: number; ask: number }>) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile)) {
        return;
      }

      const marginContainer =
        isDefined(tile.properties.rfs) && tile.properties.rfs.status === 'STREAMED'
          ? tile.properties.rfs
          : tile.type === 'FxSpot' && tile.properties.margin?.isTieringSuccess === true
            ? tile.properties.margin
            : undefined;

      if (isNotDefined(marginContainer)) {
        return;
      }

      marginContainer.commercialMarginSpotPoints = {
        bid: action.payload.bid,
        ask: action.payload.ask,
      };

      // initialize the default margin on first update (ESP tiering path only)
      if (
        tile.type === 'FxSpot' &&
        tile.properties.margin?.isTieringSuccess === true &&
        isNotDefined(tile.properties.margin.commercialMarginDefaultSpotPoints) &&
        (isNotDefined(tile.properties.rfs) || tile.properties.rfs.status !== 'STREAMED')
      ) {
        tile.properties.margin.commercialMarginDefaultSpotPoints = {
          bid: action.payload.bid,
          ask: action.payload.ask,
        };
      }
    },

    cashCommercialSwapMarginUpdated: (state, action: PayloadAction<{ tileId: string; bid: number; ask: number }>) => {
      const tile = state[action.payload.tileId];

      if (
        isNotDefined(tile) ||
        (tile.type !== 'FxFwd' && tile.type !== 'FxNdf') ||
        isNotDefined(tile.properties.rfs) ||
        tile.properties.rfs.status !== 'STREAMED'
      ) {
        return;
      }

      tile.properties.rfs.commercialMarginSwapPoints = { bid: action.payload.bid, ask: action.payload.ask };
    },

    cashCommercialMarginCurrencyToggled: (state, action: PayloadAction<{ tileId: string }>) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile)) {
        return;
      }

      const marginContainer =
        isDefined(tile.properties.rfs) && tile.properties.rfs.status === 'STREAMED'
          ? tile.properties.rfs
          : tile.type === 'FxSpot' && tile.properties.margin?.isTieringSuccess === true
            ? tile.properties.margin
            : undefined;

      if (isNotDefined(marginContainer)) {
        return;
      }

      marginContainer.commercialMarginAmountCurrencyIndex =
        marginContainer.commercialMarginAmountCurrencyIndex === 1 ? 2 : 1;
    },

    // RFS

    cashRfsQuoteRequested: (
      state,
      action: PayloadAction<{ tileId: string; rfsId: string; requestedAt: Timestamp }>,
    ) => {
      const tile = state[action.payload.tileId];

      if (isNotDefined(tile)) {
        return;
      }

      // if a quote is received after the execution, ignore it
      if (isDefined(tile.execution)) {
        return;
      }

      tile.properties.rfs = {
        status: 'PENDING',
        rfsId: action.payload.rfsId,
        requestedAt: action.payload.requestedAt,
      };

      tile.properties.error = undefined;
    },

    cashRfsQuoteReceived: (
      state,
      action: PayloadAction<{
        tileId: string;
        backTimestamp: Timestamp;
        frontTimestamp: Timestamp;
        rfsId: string;
        quoteType: 'QUOTE.REPLY' | 'QUOTE.WITHDRAW';
        reply: ReceivedCashQuoteReply;
      }>,
    ) => {
      const tile = state[action.payload.tileId];

      // is tile exists
      if (isNotDefined(tile)) {
        return;
      }

      // if a quote is received after the execution, ignore it
      if (isDefined(tile.execution)) {
        return;
      }

      // if a quote is received when the rfs status is not pending or already streamed, ignore it
      if (
        isNotDefined(tile.properties.rfs) ||
        (tile.properties.rfs.status !== 'PENDING' && tile.properties.rfs.status !== 'STREAMED')
      ) {
        return;
      }

      // if the quote comes from a different (e.g. stale, never-cancelled) rfsId than the one the
      // tile is currently pending/streaming on, ignore it: a late quote from a superseded RFS must
      // never overwrite the currently active one
      if (tile.properties.rfs.rfsId !== action.payload.rfsId) {
        return;
      }

      const { backTimestamp, frontTimestamp, rfsId, quoteType, reply } = action.payload;

      const quote = {
        requestId: rfsId,
        traderId: reply.TraderId,
        quoteId: reply.QuoteId,
        quoteType: quoteType === 'QUOTE.REPLY' ? 'Tradeable' : 'Withdraw',
        distributionStream: reply.DistributionStream,
        rfsRemainingTime: reply.RfsWindow,
        rfsWindow:
          isDefined(tile.properties.rfs) && tile.properties.rfs.status === 'STREAMED'
            ? tile.properties.rfs.quote.rfsWindow
            : reply.RfsWindow,
        frontTimestamp: Number(frontTimestamp),
        backTimestamp: Number(backTimestamp),

        midAllInRate: isDefined(reply.MidAllInRate) ? Number(reply.MidAllInRate) : undefined,

        forwardPointsWithMargin: isDefined(reply.ForwardPointsWithMargin)
          ? {
              bid: +reply.ForwardPointsWithMargin.Bid,
              ask: +reply.ForwardPointsWithMargin.Ask,
            }
          : { bid: 0, ask: 0 },

        forwardPointsWithoutMargin: isDefined(reply.ForwardPointsWithoutMargin)
          ? {
              bid: +reply.ForwardPointsWithoutMargin.Bid,
              ask: +reply.ForwardPointsWithoutMargin.Ask,
            }
          : { bid: 0, ask: 0 },

        spotWithMargin: isDefined(reply.SpotWithMargin)
          ? {
              bid: +reply.SpotWithMargin.Bid,
              ask: +reply.SpotWithMargin.Ask,
            }
          : { bid: 0, ask: 0 },

        // instead of ESP stream, the backend sends margin in PPS directly, and not in points
        // to have one behavior for all the tiles, we convert the PPS to points
        defaultSpotMarginPoints: isDefined(reply.DefaultSpotMargin)
          ? {
              bid: +reply.DefaultSpotMargin.Bid * PPS_TO_POINTS_FACTOR,
              ask: +reply.DefaultSpotMargin.Ask * PPS_TO_POINTS_FACTOR,
            }
          : { bid: 0, ask: 0 },

        spotWithoutMargin: isDefined(reply.SpotWithoutMargin)
          ? {
              bid: +reply.SpotWithoutMargin.Bid,
              ask: +reply.SpotWithoutMargin.Ask,
            }
          : { bid: 0, ask: 0 },

        // instead of ESP stream, the backend sends margin in PPS directly, and not in points
        // to have one behavior for all the tiles, we convert the PPS to points
        defaultForwardMarginPoints: isDefined(reply.DefaultForwardMarginPoints)
          ? {
              bid: +reply.DefaultForwardMarginPoints.Bid * PPS_TO_POINTS_FACTOR,
              ask: +reply.DefaultForwardMarginPoints.Ask * PPS_TO_POINTS_FACTOR,
            }
          : {
              bid: 0,
              ask: 0,
            },

        predealChecks: reply.PredealChecks.map((predealCheck) => {
          return predealCheck.Type === 'Icon'
            ? ({
                label: predealCheck.Label,
                statusHealth: predealCheck.StatusHealth,
                type: predealCheck.Type,
              } satisfies PredealCheckStatusIcon)
            : ({
                label: predealCheck.Label,
                statusHealth: predealCheck.StatusHealth,
                type: predealCheck.Type,
                statusValue: predealCheck.StatusValue,
              } satisfies PredealCheckStatusLabel);
        }),
      } satisfies CashQuote;

      const previousStreamedRfs = tile.properties.rfs.status === 'STREAMED' ? tile.properties.rfs : undefined;

      const rfs = {
        status: 'STREAMED',
        rfsId,
        requestedAt: tile.properties.rfs.requestedAt,

        quote,

        commercialMarginSpotPoints: previousStreamedRfs?.commercialMarginSpotPoints ?? {
          bid: Number(reply.DefaultSpotMargin?.Bid ?? 0),
          ask: Number(reply.DefaultSpotMargin?.Ask ?? 0),
        },

        commercialMarginDefaultSpotPoints: previousStreamedRfs?.commercialMarginDefaultSpotPoints ?? {
          bid: Number(reply.DefaultSpotMargin?.Bid ?? 0),
          ask: Number(reply.DefaultSpotMargin?.Ask ?? 0),
        },

        commercialMarginSwapPoints: previousStreamedRfs?.commercialMarginSwapPoints ?? {
          bid: Number(reply.DefaultForwardMarginPoints?.Bid ?? 0),
          ask: Number(reply.DefaultForwardMarginPoints?.Ask ?? 0),
        },

        commercialMarginDefaultSwapPoints: previousStreamedRfs?.commercialMarginDefaultSwapPoints ?? {
          bid: Number(reply.DefaultForwardMarginPoints?.Bid ?? 0),
          ask: Number(reply.DefaultForwardMarginPoints?.Ask ?? 0),
        },

        commercialMarginAmountCurrencyIndex: previousStreamedRfs?.commercialMarginAmountCurrencyIndex ?? 2,
        negotiations: previousStreamedRfs?.negotiations ?? [],
      } satisfies RfsPrice;

      tile.properties.rfs = rfs;
      tile.properties.error = undefined;
    },

    cashRequestCanceled: (state, action: PayloadAction<{ tileId: string }>) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile)) {
        // || (tile.type !== 'FxFwd' && tile.type !== 'FxNdf')) {
        return;
      }

      tile.properties.rfs = undefined;
    },

    // only used to log timeout occurrences
    cashHeartbeatTimeout: (_state, _action: PayloadAction<{ tileId: string }>) => {},

    cashExecutionPending: (state, action: PayloadAction<{ tileId: string; executionId: string }>) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile)) {
        return;
      }

      tile.execution = {
        status: 'PENDING',
        executionId: action.payload.executionId,
      };

      if (tile.type === 'FxFwd' || tile.type === 'FxNdf') {
        tile.properties.rfs = undefined;
      }
    },

    cashExecutionSuccess: (state, action: PayloadAction<{ tileId: string }>) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile) || isNotDefined(tile.execution)) {
        return;
      }

      tile.execution = {
        status: 'SUCCESS',
        executionId: tile.execution.executionId,
      };
    },

    cashExecutionRejected: (state, action: PayloadAction<{ tileId: string }>) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile) || isNotDefined(tile.execution)) {
        return;
      }

      tile.execution = {
        status: 'REJECTED',
        executionId: tile.execution.executionId,
      };
    },

    cashRejectedExecutionReset: (state, action: PayloadAction<{ tileId: string }>) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile) || isNotDefined(tile.execution)) {
        return;
      }

      tile.execution = undefined;
    },

    cashExecutionReset: (
      state,
      action: PayloadAction<{ tileId: string; userPreferences: UserPreferencesState; sessionId: string }>,
    ) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile) || isNotDefined(tile.execution)) {
        return;
      }

      const newTile = generateCashSpotTile(
        tile.properties.currencyPair,
        action.payload.userPreferences,
        action.payload.sessionId,
      );

      state[action.payload.tileId] = {
        ...newTile,
        clientId: tile.clientId,
        properties: {
          ...newTile.properties,

          // keep some properties
          notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
          notional: tile.properties.notional,

          maturityDate: tile.properties.maturityDate,
          maturityDateTenor: tile.type === 'FxSpot' ? tile.properties.maturityDateTenor : undefined,

          // spot margin
          margin: tile.type === 'FxSpot' ? tile.properties.margin : undefined,
        },
      };
    },

    cashErrorOccurred: (
      state: CashById,
      action: PayloadAction<{
        tileId: string;
        errorType: 'tile' | 'rate';
        errorCode: string;
        errorMessage: string;
        multipass?: { errorCode: string; sessionId: string; predealChecks: PredealCheck[] };
      }>,
    ) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile)) {
        return;
      }

      tile.properties.error = {
        type: action.payload.errorType,
        code: action.payload.errorCode,
        message: action.payload.errorMessage,

        multipass: isDefined(action.payload.multipass)
          ? {
              code: action.payload.multipass.errorCode,
              sessionId: action.payload.multipass.sessionId,
              predealChecks: action.payload.multipass.predealChecks,
            }
          : undefined,
      };
    },

    cashReset: (
      state,
      action: PayloadAction<{ tileId: string; userPreferences: UserPreferencesState; sessionId: string }>,
    ) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile)) {
        return;
      }

      state[action.payload.tileId] = generateCashSpotTile(
        tile.properties.currencyPair,
        action.payload.userPreferences,
        action.payload.sessionId,
      );
    },
  },

  extraReducers: (builder) => {
    reduceCashStateOnSharedTabRemoved(builder);

    //  ██████╗ ██████╗ ██╗██████╗     ████████╗██╗██╗     ███████╗
    // ██╔════╝ ██╔══██╗██║██╔══██╗    ╚══██╔══╝██║██║     ██╔════╝
    // ██║  ███╗██████╔╝██║██║  ██║       ██║   ██║██║     █████╗
    // ██║   ██║██╔══██╗██║██║  ██║       ██║   ██║██║     ██╔══╝
    // ╚██████╔╝██║  ██║██║██████╔╝       ██║   ██║███████╗███████╗
    //  ╚═════╝ ╚═╝  ╚═╝╚═╝╚═════╝        ╚═╝   ╚═╝╚══════╝╚══════╝

    builder.addCase(
      addTile,
      (
        state,
        {
          payload: {
            tile: { tileId, productType },
            userPreferences,
            sessionId,
          },
        },
      ) => {
        // already defined
        if (isDefined(state[tileId])) {
          return state;
        }

        // not for a cash
        if (productType !== 'Cash') {
          return state;
        }

        // ProductType === Cash: create a new tile
        return {
          ...state,
          [tileId]: generateCashSpotTile(undefined, userPreferences, sessionId),
        };
      },
    );

    builder.addCase(duplicateTile, (state, { payload: { sourceTileId, newTileId, sessionId } }) => {
      // source undefined
      const sourceTile = state[sourceTileId];

      if (isNotDefined(sourceTile) || isNotDefined(sessionId)) {
        return state;
      }

      // already defined
      if (isDefined(state[newTileId])) {
        return state;
      }

      return {
        ...state,
        [newTileId]: buildDuplicatedCashTile(sourceTile, sessionId),
      };
    });

    cashRepriceQuoteCashReducer(builder);

    builder.addCase(
      updateTileProductType,
      (state, { payload: { tileId, productType, userPreferences, sessionId } }) => {
        // tileId exists in state and productType !== Cash: remove the tile
        if (isDefined(state[tileId]) && productType !== 'Cash') {
          delete state[tileId];
          return;
        }

        // tileId not in state and productType === Cash: create a new tile
        if (isNotDefined(state[tileId]) && productType === 'Cash') {
          state[tileId] = generateCashSpotTile(undefined, userPreferences, sessionId);
        }
      },
    );

    builder.addCase(removeTile, (state, { payload: tileId }) => {
      if (isNotDefined(state[tileId])) {
        return state;
      }

      delete state[tileId];
    });

    // ████████╗██████╗  █████╗ ██████╗ ███████╗     ██████╗ █████╗ ██████╗ ████████╗██╗   ██╗██████╗ ███████╗
    // ╚══██╔══╝██╔══██╗██╔══██╗██╔══██╗██╔════╝    ██╔════╝██╔══██╗██╔══██╗╚══██╔══╝██║   ██║██╔══██╗██╔════╝
    //    ██║   ██████╔╝███████║██║  ██║█████╗      ██║     ███████║██████╔╝   ██║   ██║   ██║██████╔╝█████╗
    //    ██║   ██╔══██╗██╔══██║██║  ██║██╔══╝      ██║     ██╔══██║██╔═══╝    ██║   ██║   ██║██╔══██╗██╔══╝
    //    ██║   ██║  ██║██║  ██║██████╔╝███████╗    ╚██████╗██║  ██║██║        ██║   ╚██████╔╝██║  ██║███████╗
    //    ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝╚═════╝ ╚══════╝     ╚═════╝╚═╝  ╚═╝╚═╝        ╚═╝    ╚═════╝ ╚═╝  ╚═╝╚══════╝

    builder.addMatcher(isAnyOf(cashApi.endpoints.checkCashByTradeCapture.matchPending), (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      const currentCash = state[tileId];

      // cash must be not already executed
      if (isNotDefined(currentCash) || isNotDefined(currentCash.tradeCapture) || isDefined(currentCash.execution)) {
        return;
      }

      const currentTradeCapture = currentCash.tradeCapture;

      // optimistic updates
      const { maturityDate: updatedMaturityDate, ...otherProperties } = action.meta.arg.originalArgs.properties;

      // check if the maturity date is the same date, or the same tenor
      // => don't update it, because if it is the case, the prod will not return an updated value
      //    and so, the prod will not restore the good value
      const maturityDate =
        isDefined(updatedMaturityDate) &&
        updatedMaturityDate !== currentCash.properties.maturityDate &&
        updatedMaturityDate.toUpperCase() !== currentCash.properties.maturityDateTenor
          ? updatedMaturityDate
          : currentCash.properties.maturityDate;

      const isDateUnresolved = isDefined(maturityDate) && !isIsoDate(maturityDate);

      const pendingTradeCapture: TradeCaptureChecked<FxCash['properties']> =
        currentTradeCapture.status === 'INITIALIZED' ||
        currentTradeCapture.status === 'CHECKING' ||
        currentTradeCapture.status === 'ERROR'
          ? {
              status: 'CHECKING',
              sessionId: currentTradeCapture.sessionId,
              version: currentTradeCapture.version,
              isReadyToPrice: false,
              isPriceObsolete: false,
              isProductObsolete: false,

              warnings: {},
              errors: {},
            }
          : {
              status: 'CHECKING',
              sessionId: currentTradeCapture.sessionId,
              version: currentTradeCapture.version,
              isReadyToPrice: isDateUnresolved ? false : currentTradeCapture.isReadyToPrice,
              isPriceObsolete: currentTradeCapture.isPriceObsolete,
              isProductObsolete: currentTradeCapture.isProductObsolete,

              warnings: currentTradeCapture.warnings,
              errors: currentTradeCapture.errors,
            };

      // Mutate the draft directly to avoid union type distribution issues.
      // otherProperties comes from CheckCashParams (ObjectWithUndefinedPropertiesNullable) which
      // introduces null for optional fields — Object.assign is permissive and compatible at runtime.
      //
      // Pricing-specific fields (rfs, error) are pinned to their current
      // values so the optimistic update never overwrites them — even when originalArgs.properties
      // is a spread of a full tile (e.g. during duplication). Without this, the source tile's
      // active rfsId would be copied onto the new tile, causing the cancel listener to send a
      // cancel for the source tile's RFS stream.
      Object.assign(currentCash.properties, {
        maturityDate,
        ...otherProperties,
        rfs: currentCash.properties.rfs,
        error: currentCash.properties.error,
      });
      currentCash.tradeCapture = pendingTradeCapture;
    });

    builder.addMatcher(isAnyOf(cashApi.endpoints.checkCashByTradeCapture.matchFulfilled), (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      const cashFromTradeCapture = action.payload[tileId];

      if (isNotDefined(cashFromTradeCapture)) {
        return;
      }

      const currentCash = state[tileId];

      const isSpotWithInvertedCurrencyPair =
        (action.meta.arg.originalArgs.type === 'FxFwd' &&
          action.meta.arg.originalArgs.properties.isSpotWithInvertedCurrencyPair) ??
        (currentCash?.type === 'FxFwd' ? currentCash.properties.isSpotWithInvertedCurrencyPair : false) ??
        false;

      // cash must be not already executed
      if (isNotDefined(currentCash) || isNotDefined(cashFromTradeCapture) || isDefined(currentCash.execution)) {
        return;
      }

      state[tileId] = updateCashFromTradeCapture(
        currentCash,
        isSpotWithInvertedCurrencyPair,
        currentCash.tradeCapture.sessionId,
        cashFromTradeCapture,
      );
    });

    builder.addMatcher(isAnyOf(cashApi.endpoints.checkCashByTradeCapture.matchRejected), (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      const currentCash = state[tileId];

      // cash must be not already executed
      if (isNotDefined(currentCash) || isNotDefined(currentCash.tradeCapture) || isDefined(currentCash.execution)) {
        return;
      }

      const tradeCapture = currentCash.tradeCapture;

      currentCash.tradeCapture = {
        status: 'ERROR',
        sessionId: tradeCapture.sessionId,
        version: tradeCapture.version,

        error: 'TIMEOUT',
      };
    });

    // ██████╗ ███████╗███████╗
    // ██╔══██╗██╔════╝██╔════╝
    // ██████╔╝█████╗  ███████╗
    // ██╔══██╗██╔══╝  ╚════██║
    // ██║  ██║██║     ███████║
    // ╚═╝  ╚═╝╚═╝     ╚══════╝

    // =====
    // =====
    // =====
    //
    // WARNING: can't listen rfsApi endpoints because of the dependency cycle
    // between this slice and rfsApi
    // => dispatch actions of this slice from the rfsApi endpoints
    //
    // =====
    // =====
    // =====

    // ███████╗███████╗██████╗
    // ██╔════╝██╔════╝██╔══██╗
    // █████╗  ███████╗██████╔╝
    // ██╔══╝  ╚════██║██╔═══╝
    // ███████╗███████║██║
    // ╚══════╝╚══════╝╚═╝

    builder.addMatcher(espStreamUnsubscribed.match, (state, action) => {
      const tile = state[action.payload.tileId];

      if (isNotDefined(tile) || tile.type !== 'FxSpot') {
        return;
      }

      tile.properties.margin = undefined;
    });

    // ██████╗ ███████╗ ██████╗ ██████╗ ██████╗ ██████╗     ██████╗ ██████╗ ██╗ ██████╗███████╗███████╗
    // ██╔══██╗██╔════╝██╔════╝██╔═══██╗██╔══██╗██╔══██╗    ██╔══██╗██╔══██╗██║██╔════╝██╔════╝██╔════╝
    // ██████╔╝█████╗  ██║     ██║   ██║██████╔╝██║  ██║    ██████╔╝██████╔╝██║██║     █████╗  ███████╗
    // ██╔══██╗██╔══╝  ██║     ██║   ██║██╔══██╗██║  ██║    ██╔═══╝ ██╔══██╗██║██║     ██╔══╝  ╚════██║
    // ██║  ██║███████╗╚██████╗╚██████╔╝██║  ██║██████╔╝    ██║     ██║  ██║██║╚██████╗███████╗███████║
    // ╚═╝  ╚═╝╚══════╝ ╚═════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝     ╚═╝     ╚═╝  ╚═╝╚═╝ ╚═════╝╚══════╝╚══════╝

    builder.addMatcher(mifidApi.endpoints.recordPrice.matchFulfilled, (state, action) => {
      const tile = state[action.meta.arg.originalArgs.tileId];

      if (
        isNotDefined(tile) ||
        (tile.type !== 'FxFwd' && tile.type !== 'FxNdf') ||
        tile.properties.rfs?.status !== 'STREAMED'
      ) {
        return;
      }

      tile.properties.rfs.negotiations.push({
        date: formatISO9075(action.meta.arg.originalArgs.date),
      });
    });

    // ██╗    ██╗ ██████╗ ██████╗ ██╗  ██╗███████╗██████╗  █████╗  ██████╗███████╗
    // ██║    ██║██╔═══██╗██╔══██╗██║ ██╔╝██╔════╝██╔══██╗██╔══██╗██╔════╝██╔════╝
    // ██║ █╗ ██║██║   ██║██████╔╝█████╔╝ ███████╗██████╔╝███████║██║     █████╗
    // ██║███╗██║██║   ██║██╔══██╗██╔═██╗ ╚════██║██╔═══╝ ██╔══██║██║     ██╔══╝
    // ╚███╔███╔╝╚██████╔╝██║  ██║██║  ██╗███████║██║     ██║  ██║╚██████╗███████╗
    //  ╚══╝╚══╝  ╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═╝╚══════╝╚═╝     ╚═╝  ╚═╝ ╚═════╝╚══════╝

    builder.addMatcher(getWorkspaceMatchFulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }

      // clear all products created before workspace has loaded
      for (const id of Object.keys(state)) {
        delete state[id];
      }

      action.payload.products.cash.forEach((cashProduct) => {
        state[cashProduct.tileId] = mapToCashTile(cashProduct);
      });
    });

    reduceCashOnFetchSharedWorkspace(builder);

    // ██████╗  █████╗ ████████╗ █████╗
    // ██╔══██╗██╔══██╗╚══██╔══╝██╔══██╗
    // ██║  ██║███████║   ██║   ███████║
    // ██║  ██║██╔══██║   ██║   ██╔══██║
    // ██████╔╝██║  ██║   ██║   ██║  ██║
    // ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝

    updateFixingSource(builder);
  },

  selectors: {
    getAllCashProducts: (state) => Object.entries(state).map(([tileId, product]) => ({ tileId, ...product })),
  },
});

export const {
  cashNotionalCurrencyChanged,
  cashSpotTieringUpdated,
  cashSpotTieringError,
  cashClientIdUpdated,
  cashCommercialSpotMarginUpdated,
  cashCommercialSwapMarginUpdated,
  cashCommercialMarginCurrencyToggled,

  cashReset,

  cashRfsQuoteRequested,
  cashRfsQuoteReceived,
  cashHeartbeatTimeout,
  cashRequestCanceled,
  cashErrorOccurred,

  cashExecutionPending,
  cashExecutionSuccess,
  cashExecutionRejected,
  cashRejectedExecutionReset,
  cashExecutionReset,
} = cashSlice.actions;

export const { getAllCashProducts } = cashSlice.selectors;
